import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ClipboardCheck, Eye } from 'lucide-react';
import { Pagination } from '../components/ui/Pagination';
import { api, getPropertyCoverImageUrl, PROPERTY_IMAGE_PLACEHOLDER } from '../services/api';

type DeliveryInspectionStatus = 'pending' | 'in_progress' | 'completed';

type DeliveryInspectionItem = {
  id: string;
  status: DeliveryInspectionStatus;
  type?: 'entry' | 'exit';
  createdAt: string;
  completedAt?: string | null;
  tenants: {
    id: string;
    name: string;
    unitNumber: string | null;
    properties: {
      id: string;
      name: string;
      coverImage?: string | null;
    } | null;
  } | null;
};

const limit = 20;

function StatusBadge({ status }: { status: DeliveryInspectionStatus }) {
  const config: Record<DeliveryInspectionStatus, { label: string; className: string }> = {
    pending: { label: 'Pendiente', className: 'bg-amber-100 text-amber-800' },
    in_progress: { label: 'En progreso', className: 'bg-sky-100 text-sky-800' },
    completed: { label: 'Completado', className: 'bg-green-100 text-green-800' },
  };
  const { label, className } = config[status] ?? config.pending;
  return <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${className}`}>{label}</span>;
}

export const DeliveryInspectionList: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  const { data: res, isLoading, isError } = useQuery({
    queryKey: ['my-delivery-inspections', page],
    queryFn: () => api.getMyDeliveryInspections({ page, limit }),
    staleTime: 60_000,
  });

  const inspections: DeliveryInspectionItem[] = useMemo(() => {
    const list = res?.data ?? [];
    return Array.isArray(list) ? list : [];
  }, [res]);
  const total = res?.total ?? 0;
  const totalPages = res?.totalPages ?? 0;

  const openInspection = (inspection: DeliveryInspectionItem) => {
    if (inspection.status === 'completed') return;
    navigate(`/delivery-inspection/${inspection.id}`);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <ClipboardCheck className="w-7 h-7 text-primary" />
          Inspecciones de entrega y salida
        </h1>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        {isError && (
          <div className="mx-4 mt-3 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
            No fue posible cargar tus inspecciones.
          </div>
        )}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <>
            <div className="w-full min-w-0 max-w-full overflow-x-auto">
              <table className="w-full min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Propiedad</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Inquilino</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unidad</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Completado</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acción</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {inspections.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-6 py-8 text-sm text-gray-500 text-center">
                        No tienes inspecciones asignadas por ahora.
                      </td>
                    </tr>
                  ) : (
                    inspections.map((inspection) => {
                      const tenant = inspection.tenants;
                      const property = tenant?.properties;
                      const isOpenable = inspection.status !== 'completed';
                      return (
                        <tr
                          key={inspection.id}
                          className={isOpenable ? 'cursor-pointer hover:bg-gray-50' : ''}
                          onClick={() => openInspection(inspection)}
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div className="flex items-center gap-3">
                              <img
                                src={property?.coverImage ? getPropertyCoverImageUrl(property.coverImage) : PROPERTY_IMAGE_PLACEHOLDER}
                                alt=""
                                className="w-10 h-10 rounded object-cover flex-shrink-0"
                              />
                              <div className="font-medium text-gray-900">{property?.name || '—'}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {inspection.type === 'exit' ? (
                              <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-violet-100 text-violet-800">
                                Salida
                              </span>
                            ) : (
                              <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-800">
                                Entrada
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{tenant?.name || '—'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{tenant?.unitNumber || '—'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {new Date(inspection.createdAt).toLocaleDateString('es-MX', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                            })}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {inspection.completedAt
                              ? new Date(inspection.completedAt).toLocaleDateString('es-MX', {
                                  day: 'numeric',
                                  month: 'long',
                                  year: 'numeric',
                                })
                              : '—'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <StatusBadge status={inspection.status} />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                openInspection(inspection);
                              }}
                              disabled={!isOpenable}
                              className="inline-flex items-center justify-center p-2 rounded-lg text-sm font-medium bg-white text-primary border border-gray-200 hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                              title={isOpenable ? 'Abrir inspección' : 'Inspección completada'}
                              aria-label={isOpenable ? 'Abrir inspección' : 'Inspección completada'}
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <Pagination
                page={page}
                totalPages={totalPages}
                total={total}
                limit={limit}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

